import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { BANKING_COUNTRIES, DEFAULT_BANK_COUNTRY } from "../lib/bankingLocale";
import { Loader2, Check, Globe } from "lucide-react";
import { toast } from "sonner";

export function CountryRegionTile() {
    const { user, refreshMe } = useAuth();
    const [current, setCurrent] = useState(user?.country || DEFAULT_BANK_COUNTRY);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        setCurrent(user?.country || DEFAULT_BANK_COUNTRY);
    }, [user?.country]);

    const save = async (code) => {
        try {
            setBusy(true);
            await api.patch("/users/me", { country: code });
            setCurrent(code);
            await refreshMe();
            const label = BANKING_COUNTRIES.find((c) => c.code === code)?.name || code;
            toast.success(`Country set to ${label}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="surface p-5" data-testid="country-region-tile">
            <div className="flex items-start gap-2 mb-1">
                <Globe className="h-4 w-4 text-[#00BF0D] mt-0.5 shrink-0" />
                <h3 className="font-heading text-lg">Country / region</h3>
            </div>
            <p className="text-xs text-white/55 mt-1 max-w-md mb-4">
                Controls bank form labels and examples (for example UK sort code vs US routing number).
                FairShare defaults to the United Kingdom.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {BANKING_COUNTRIES.map((c) => (
                    <button
                        key={c.code}
                        type="button"
                        onClick={() => save(c.code)}
                        disabled={busy}
                        className={`rounded-lg border px-3 py-2.5 text-left text-xs transition-colors inline-flex items-center justify-between gap-2 ${
                            current === c.code
                                ? "border-[#00BF0D]/35 bg-[#00BF0D]/8 text-[#00BF0D]"
                                : "border-white/[0.08] bg-white/[0.025] text-white/75 hover:text-white hover:bg-white/[0.05]"
                        }`}
                        data-testid={`country-${c.code}`}
                    >
                        <span>
                            <span className="block font-semibold">{c.name}</span>
                            <span className="block text-[10px] text-white/40 font-mono mt-0.5">{c.code}</span>
                        </span>
                        {current === c.code && <Check className="h-3.5 w-3.5 shrink-0" />}
                    </button>
                ))}
            </div>
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-white/40 mt-2" />}
        </div>
    );
}
